<template>
  <DownloadsModal
    v-if="showDownloadsModal"
    :plants="plants"
    @closeModal="showDownloadsModal = false"
  />
  <ProductsCartModal
    v-if="showProductsCartModal"
    :type="productCartType"
    :plants="plants"
    @closeModal="showProductsCartModal = false"
  />
  <div data-cy="schedule-b-view">
    <div class="schedule-actions">
      <LoadingButton
        type="button"
        theme-color="primary"
        :loading="!viewLoaded"
        @click="showDownloadsModal = true"
        data-cy="download-schedule-b"
        >Download Schedule B</LoadingButton
      >

      <div class="badge-container-wrapper" v-if="canOpenOrModifyCarts">
        <BadgeContainer
          v-for="action in cartActions"
          :key="action.subApplication"
          content="content"
          rounded="full"
          :theme-color="action.theme"
          size="large"
          :cutout-border="true"
          data-cy="badge-container"
        >
          <LoadingButton
            type="button"
            :theme-color="action.theme"
            :loading="!viewLoaded || !action.loaded"
            :disabled="action.count === 0"
            @click="action.onClick"
            :data-cy="`complete-order-${action.subApplication}`"
            >{{ action.name
            }}<font-awesome-icon icon="fa-solid fa-cart-shopping" />
          </LoadingButton>
          <template v-slot:content>{{ action.count }}</template>
        </BadgeContainer>
      </div>
    </div>
    <ScheduleBTable
      v-if="viewLoaded"
      :plants="plants"
      :statusList="statusList"
      :canModifyCarts="canOpenOrModifyCarts"
    />
    <TableSkeleton v-else />
  </div>
</template>

<script setup lang="ts">
import { useMainStore } from "@/stores/useMainStore";
import ScheduleBTable from "./ScheduleBTable.vue";
import { computed, onBeforeMount, ref } from "vue";
import type { IdAndName } from "@/Domain/IdAndName";
import { handleResponsePromiseAsync } from "@/shared/helpers/apiHelper";
import { api } from "@/api";
import { EnumMerchStatus } from "@/Domain/EnumMerchStatus";
import { BadgeContainer } from "@progress/kendo-vue-indicators";
import { SubApplicationType } from "@/Domain/SubApplicationType";
import DownloadsModal from "@/views/scheduleB/DownloadsModal.vue";
import { useScheduleBStore } from "@/stores/useScheduleBStore";
import ProductsCartModal from "@/views/scheduleB/ProductsCartModal.vue";
import LoadingButton from "@/shared/ui/inputs/LoadingButton.vue";
import TableSkeleton from "@/shared/ui/skeletons/TableSkeleton.vue";
import { EnumCompanyStatus } from "@/Domain/EnumCompanyStatus";
import { ProductAppGroup } from "@/shared/access/RoleGroups";

const main = useMainStore();
const scheduleStore = useScheduleBStore();

const plants = ref<IdAndName[]>([]);
const viewLoaded = ref(false);
const showDownloadsModal = ref(false);
const showProductsCartModal = ref(false);
const productCartType = ref<SubApplicationType>(SubApplicationType.Add);
const gpcPlantId = ref<string>("");
const gpcPlantIdLoaded = ref(false);

const statusList = [
  EnumMerchStatus[EnumMerchStatus.Certified],
  EnumMerchStatus[EnumMerchStatus.Pending],
  EnumMerchStatus[EnumMerchStatus.Terminated],
  EnumMerchStatus[EnumMerchStatus.Rejected],
  EnumMerchStatus[EnumMerchStatus.Withdrawn],
  EnumMerchStatus[EnumMerchStatus.WebApp],
];

const canOpenOrModifyCarts = computed(() => {
  return (
    (main.companySummary &&
      main.companySummary.denials.every(
        (d) => !d.Products || d.Products.toUpperCase() !== "ALL"
      ) &&
      main.companySummary.status !== EnumCompanyStatus.Pending &&
      gpcPlantIdLoaded.value &&
      !gpcPlantId.value &&
      ProductAppGroup.roles.some((r) => main.isInRole(r))) ??
    false
  );
});

const cartActions = ref([
  {
    name: "Complete order: Add New Product(s)",
    count: scheduleStore.getCartCount(SubApplicationType.Add),
    subApplication: SubApplicationType.Add,
    loaded: false,
    theme: "success",
    onClick: () => {
      productCartType.value = SubApplicationType.Add;
      showProductsCartModal.value = true;
    },
  },
  {
    name: "Complete order: Copy Product(s)",
    count: scheduleStore.getCartCount(SubApplicationType.Copy),
    subApplication: SubApplicationType.Copy,
    loaded: false,
    theme: "primary",
    onClick: () => {
      productCartType.value = SubApplicationType.Copy;
      showProductsCartModal.value = true;
    },
  },
  {
    name: "Complete order: Terminate Product(s)",
    count: scheduleStore.getCartCount(SubApplicationType.Terminate),
    subApplication: SubApplicationType.Terminate,
    loaded: false,
    theme: "error",
    onClick: () => {
      productCartType.value = SubApplicationType.Terminate;
      showProductsCartModal.value = true;
    },
  },
]);

const getProductsInCartCountAsync = async function (subApplication: string) {
  return api.get(
    "VueGetAllMatchingProductsInCartSubApplicationAndUserLoginCount",
    {
      userLogin: main.userLogin,
      companyLogin: main.companyLogin,
      subApplication: subApplication,
    }
  );
};

const getPlantsAsync = async function () {
  return api.get("GetAuthorizedCertifiedOrPendingOwns", {
    userLogin: main.userLogin,
    companyLogin: main.companyLogin,
    certifiedOnly: false,
  });
};

const updateProductsInCartCountAsync = async function () {
  const promises = cartActions.value.map(async (action) => {
    return handleResponsePromiseAsync(
      getProductsInCartCountAsync(action.subApplication),
      (data) => {
        scheduleStore.setCartCount(action.subApplication, data);
        action.loaded = true;
      },
      "ScheduleBView",
      "updateProductsInCartCountAsync"
    );
  });
  await Promise.all(promises);
};

const updatePlantListAsync = async function () {
  return handleResponsePromiseAsync(
    getPlantsAsync(),
    (data) => {
      plants.value = data;
    },
    "ScheduleBView",
    "updatePlantListAsync"
  );
};

const updateGpcPlantIdAsync = async function () {
  return handleResponsePromiseAsync(
    api.get("GetGpcPlantId", {
      userLogin: main.userLogin,
    }),
    (data) => {
      gpcPlantId.value = data;
      gpcPlantIdLoaded.value = true;
    },
    "ScheduleBView",
    "updateGpcPlantId"
  );
};

const onCartCountUpdate = function (type: SubApplicationType, count: number) {
  const action = cartActions.value.find(
    (action) => action.subApplication === type
  );
  if (action) {
    action.count = count;
  }
};

scheduleStore.handleCartCountUpdate = onCartCountUpdate;

onBeforeMount(async () => {
  updateGpcPlantIdAsync();
  updateProductsInCartCountAsync();
  await updatePlantListAsync();
  viewLoaded.value = true;
});
</script>

<style scoped lang="scss">
.schedule-actions {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-bottom: 20px;
  flex-wrap: wrap;

  .badge-container-wrapper {
    display: flex;
    gap: 1rem;
    flex-wrap: wrap;
    justify-content: flex-end;
  }

  .fa-cart-shopping {
    margin-left: 0.5rem;
    width: 12px;
  }
}
</style>
